import React, { useState, useEffect } from "react";
import {
    Autocomplete,
    Box,
    Typography,
    FormControlLabel,
    Checkbox,
    Button,
    Card,
    CardContent,
    CardActions,
    Divider,
    Grid,
    TextField,
    MenuItem,
    IconButton,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper
} from '@mui/material';
import { CheckCircle, ErrorOutline, Search as SearchIcon, Verified } from '@mui/icons-material';
import { categoryOptions, idGrupoPlanificacionOptions, lineaKUTOptions, codigosProveedores, fetchCategoryOptions, fetchCodigosProveedores } from '../../constantes/constants';
import { SapService } from '../../services/api';

const ProviderStep = ({
    sameProvider, setSameProvider,
    sameCategoria, setSameCategoria,
    sameGrupoDePlanificacion, setSameGrupoDePlanificacion,
    sameLineaKUT, setSameLineaKUT,
    selectedProvider, setSelectedProvider,
    selectedCategoria, setSelectedCategoria,
    selectedGrupoDePlanificacion, setSelectedGrupoDePlanificacion,
    selectedLineaKUT, setSelectedLineaKUT,
    handleNext, setMaterials, materials, proveedoresDatos, setProveedoresDatos
}) => {


    const [searchCode, setSearchCode] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [categoryOptionsv2, setCategoryOptionsV2] = useState([]);
    const [providerCodesV2, setProviderCodesV2] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [options, provCodes] = await Promise.all([
                    fetchCategoryOptions(),
                    fetchCodigosProveedores()
                ]);
                setCategoryOptionsV2(options);
                setProviderCodesV2(provCodes);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, []);

    const handleSearch = async () => {
        if (!searchCode) return;
        setLoading(true);
        try {
            const token = await SapService.login();
            const url = `https://my360131.sapbydesign.com/sap/byd/odata/ana_businessanalytics_analytics.svc/RPBUPSPP_Q0002QueryResults?$select=CPHONE_NR,CCITY_NAME,TCOUNTRY_CODE,CTAX_TYPE,CTAX_ID_NR,CBP_UUID,TBP_UUID&$filter=(CBP_UUID eq '${searchCode}')&$top=1&$format=json`;

            const value = await SapService.getData(url, token);

            const results = value.d.results.map(element => ({
                Proveedor: element.CBP_UUID,
                Tproveedor: element.TBP_UUID,
                idproveedor: providerCodesV2.find((p) => p.idproveedor === element.CBP_UUID)?.value ?? "900"
            }));

            // In the original logic, there was a filter here that seemed redundant if the query already filters by ID, 
            // but keeping the logic similar to ensure same behavior:
            const finalResults = results.filter(provider => provider.Proveedor.includes(searchCode));

            setSearchResults(finalResults);
            setOpenDialog(true);
        } catch (error) {
            console.error("Search error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectProvider = (provider) => {
        // Replaced alert with just setting logic, logic can be verified in next steps
        // console.log("Selected Provider:", provider);
        setSelectedProvider(provider);
        setOpenDialog(false);
    };

    const handleCheckboxChange = (e, type) => {
        const checked = e.target.checked;
        switch (type) {
            case 'provider': setSameProvider(checked); break;
            case 'categoria': setSameCategoria(checked); break;
            case 'grupoDePlanificacion': setSameGrupoDePlanificacion(checked); break;
            case 'lineaKUT': setSameLineaKUT(checked); break;
            default: break;
        }
    };

    const handleNextClick = () => {
        const updatedMaterials = materials.map((material) => ({
            ...material,
            proveedor: sameProvider ? selectedProvider : material.proveedor,
            categoriaProducto: sameCategoria ? selectedCategoria : material.categoriaProducto,
            idGrupoPlanificacion: sameGrupoDePlanificacion ? selectedGrupoDePlanificacion : material.idGrupoPlanificacion,
            lineaKUT: sameLineaKUT ? selectedLineaKUT : material.lineaKUT
        }));
        setMaterials(updatedMaterials);
        handleNext();
    };

    const uniqueCategoryOptions = Array.from(
        new Map(categoryOptionsv2.map((cat) => [cat.value, cat])).values()
    );

    return (
        <Card sx={{ maxWidth: 800, margin: 'auto', mt: 4, borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom align="center" sx={{ fontWeight: 700, color: 'primary.main', mb: 3 }}>
                    Configuración Global de Materiales
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    Defina valores por defecto para acelerar el proceso de ingreso.
                </Typography>
                <Divider sx={{ mb: 4 }} />

                <Grid container spacing={3}>

                    {/* Setting Section Component to reduce repetition could be done here, but keeping inline for clarity first */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, borderColor: sameCategoria ? 'primary.main' : 'divider' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle1" fontWeight="600">
                                    Categoría de Producto
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={sameCategoria}
                                            onChange={(e) => handleCheckboxChange(e, 'categoria')}
                                            color="primary"
                                        />
                                    }
                                    label="Aplicar a todos"
                                />
                            </Box>

                            {sameCategoria ? (
                                <Autocomplete
                                    options={uniqueCategoryOptions}
                                    getOptionLabel={(option) => option.label}
                                    value={uniqueCategoryOptions.find((cat) => cat.value === selectedCategoria) || null}
                                    onChange={(_, newValue) => setSelectedCategoria(newValue ? newValue.value : "")}
                                    renderInput={(params) => <TextField {...params} label="Seleccionar Categoría" placeholder="Busque una categoría..." fullWidth />}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Se especificará individualmente por material.
                                </Typography>
                            )}
                        </Paper>
                    </Grid>

                    {/* Grupo de Planificación */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, borderColor: sameGrupoDePlanificacion ? 'primary.main' : 'divider' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle1" fontWeight="600">
                                    Grupo de Planificación
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={sameGrupoDePlanificacion}
                                            onChange={(e) => handleCheckboxChange(e, 'grupoDePlanificacion')}
                                            color="primary"
                                        />
                                    }
                                    label="Aplicar a todos"
                                />
                            </Box>

                            {sameGrupoDePlanificacion ? (
                                <TextField
                                    select
                                    label="Seleccionar Grupo"
                                    value={selectedGrupoDePlanificacion}
                                    onChange={(e) => setSelectedGrupoDePlanificacion(e.target.value)}
                                    fullWidth
                                >
                                    {idGrupoPlanificacionOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Se especificará individualmente por material.
                                </Typography>
                            )}
                        </Paper>
                    </Grid>

                    {/* Línea KUT */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, borderColor: sameLineaKUT ? 'primary.main' : 'divider' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle1" fontWeight="600">
                                    Línea de Producto (KUT)
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={sameLineaKUT}
                                            onChange={(e) => handleCheckboxChange(e, 'lineaKUT')}
                                            color="primary"
                                        />
                                    }
                                    label="Aplicar a todos"
                                />
                            </Box>

                            {sameLineaKUT ? (
                                <TextField
                                    select
                                    label="Seleccionar Línea"
                                    value={selectedLineaKUT}
                                    onChange={(e) => setSelectedLineaKUT(e.target.value)}
                                    fullWidth
                                >
                                    {lineaKUTOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Se especificará individualmente por material.
                                </Typography>
                            )}
                        </Paper>
                    </Grid>

                </Grid>

                <Box sx={{ mt: 4, bgcolor: 'background.default', p: 2, borderRadius: 2 }}>
                    <Typography variant="body2" color="text.primary" align="center">
                        Si no desea aplicar configuraciones masivas, simplemente presione <b>Siguiente</b>.
                    </Typography>
                </Box>

            </CardContent>
            <CardActions sx={{ p: 3, pt: 0, justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    size="large"
                    endIcon={<Verified />}
                    onClick={handleNextClick}
                >
                    Siguiente
                </Button>
            </CardActions>

            {/* Dialog for Provider Search (kept logic but disabled in UI main part currently as originally commented out?) 
          The original code had the provider part commented out or partially hidden. 
          If I need to enable it, I will uncomment. For now, I followed the visible logic.
      */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Búsqueda de Proveedor</DialogTitle>
                <DialogContent dividers>
                    {searchResults.length > 0 ? (
                        searchResults.map((provider) => (
                            <Box
                                key={provider.Proveedor}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1
                                }}
                            >
                                <Typography>
                                    {provider.Proveedor} - {provider.Tproveedor}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleSelectProvider(provider)}
                                >
                                    Seleccionar
                                </Button>
                            </Box>
                        ))
                    ) : (
                        <Typography align="center" color="text.secondary">No se encontraron resultados</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>

        </Card>
    );
};

export default ProviderStep;
